import {
	formatCurrencyPrecise,
	formatNumber,
	getActionTypeLabel,
} from "@dune/utils";
import {
	ACTION_BUILDING_TYPES,
	actionBuildingTypeCheckboxes,
} from "@dune/vars";
import { format } from "date-fns";
import { useFormContext } from "react-hook-form";
import { ConstructionSiteDetailsFormData } from "./useConstructionSiteDetailsForm";
import { cn } from "@dune/ui";

interface OperationDescriptiveBlockProps {
	className?: string;
	hideTitle?: boolean;
}

const OperationDescriptiveBlock = ({
	className,
	hideTitle,
}: OperationDescriptiveBlockProps) => {
	const { watch } = useFormContext<ConstructionSiteDetailsFormData>();

	const {
		streetNumber,
		streetName,
		postalCode,
		city,
		actions,
		startDate,
		deliveryDate,
		cityHallStartDate,
		totalAmountTTC,
		totalAmountHT,
		taxMode,
	} = watch();

	const formBuildingDetailsValues: Record<string, string> | undefined =
		actions?.buildingType
			? actions?.buildingDetails?.[actions.buildingType]
			: undefined;

	const selectedBuildingTypeData = actionBuildingTypeCheckboxes.find(
		(checkbox) => checkbox.value === actions?.buildingType,
	);

	const hasValidValue = (fieldName: string): boolean => {
		return !!(
			formBuildingDetailsValues?.[fieldName] &&
			formBuildingDetailsValues[fieldName] !== "" &&
			formBuildingDetailsValues[fieldName] !== "0"
		);
	};

	const hasExtension = hasValidValue("extensionSurface");

	const commonParts =
		actions?.types?.includes("SYNDIC") &&
		actions?.buildingType === "COMMON_PARTS";
	const privateWork =
		actions?.types?.includes("SYNDIC") &&
		actions?.buildingType === "PRIVATE_WORK";

	return (
		<div className={cn("flex flex-col gap-4", className)}>
			{!hideTitle && (
				<h1 className="text-[#144C65] text-md font-medium">
					Descriptif de l'opération
				</h1>
			)}
			<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-6 gap-4 bg-gray-100">
				{streetName && postalCode && city && (
					<p>
						L'opération à assurer se situe au{" "}
						<b>{`${streetNumber ? `${streetNumber} ` : ""}${streetName}, ${postalCode} ${city}.`}</b>
					</p>
				)}

				{actions?.types &&
					actions?.types.length > 0 &&
					actions?.buildingType && (
						<>
							<p>
								{!commonParts && !privateWork ? (
									<>
										Elle consiste en{" "}
										<b>
											{actions?.types
												?.map((type) => getActionTypeLabel(type))
												?.join(" et ")}{" "}
											{actions?.buildingType &&
												ACTION_BUILDING_TYPES[actions.buildingType]}
										</b>
									</>
								) : (
									<>
										Elle concerne{" "}
										<b>
											{privateWork
												? "des travaux privatifs"
												: "des parties communes"}
										</b>
									</>
								)}
								{selectedBuildingTypeData?.details &&
									formBuildingDetailsValues &&
									selectedBuildingTypeData.details
										.filter(
											(detail) =>
												detail.values.some((value) =>
													hasValidValue(value.name),
												) &&
												!detail.values.some(
													(val) =>
														val.name === "surface" ||
														val.name === "extensionSurface",
												),
										)
										.map((buildingDetail, detailIndex) => {
											const validValues = buildingDetail.values.filter(
												(value) => hasValidValue(value.name),
											);

											if (validValues.length === 0) return null;

											if (buildingDetail.values.length > 1) {
												return (
													<span key={buildingDetail.values[0].name}>
														{" "}
														{buildingDetail.label.toLowerCase()}{" "}
														{validValues.map((value, index) => {
															const fieldValue =
																formBuildingDetailsValues[value.name];
															return (
																<span key={value.name}>
																	{index > 0 && ` ${buildingDetail.separator} `}
																	<b>
																		{fieldValue &&
																		!Number.isNaN(Number(fieldValue))
																			? formatNumber(fieldValue.toString())
																			: fieldValue}
																		{value.indication && ` ${value.indication}`}
																	</b>
																</span>
															);
														})}
													</span>
												);
											}

											const fieldValue =
												formBuildingDetailsValues[
													buildingDetail.values[0].name
												];
											return (
												<span key={buildingDetail.values[0].name}>
													{detailIndex === 0 ? " " : " et "}
													{buildingDetail.label.toLowerCase()}{" "}
													<b>
														{fieldValue && !Number.isNaN(Number(fieldValue))
															? formatNumber(fieldValue.toString())
															: fieldValue}{" "}
														{buildingDetail.values[0].indication}
													</b>
												</span>
											);
										})}
								.
							</p>
							{(hasValidValue("surface") ||
								totalAmountHT ||
								totalAmountTTC) && (
								<p>
									{hasExtension ? (
										<span>
											L'extension créée{" "}
											<b>
												{formBuildingDetailsValues?.extensionSurface
													? formatNumber(
															formBuildingDetailsValues.extensionSurface,
														)
													: 0}{" "}
												m²
											</b>
											{" pour une surface totale du bâtiment de "}
											<b>
												{formBuildingDetailsValues?.surface &&
													formatNumber(formBuildingDetailsValues.surface)}{" "}
												m²
											</b>
										</span>
									) : hasValidValue("surface") ? (
										<span>
											La surface totale du bâtiment est de{" "}
											<b>
												{formBuildingDetailsValues?.surface &&
													formatNumber(formBuildingDetailsValues.surface)}{" "}
												m²
											</b>
										</span>
									) : null}
									{(totalAmountHT || totalAmountTTC) && (
										<>
											{hasExtension || hasValidValue("surface")
												? " pour un montant prévisionnel total de "
												: "Le montant prévisionnel total est de "}{" "}
											{taxMode === "HT" && (
												<b>{formatCurrencyPrecise(totalAmountHT)} HT</b>
											)}
											{taxMode === "TTC" && (
												<b>{formatCurrencyPrecise(totalAmountTTC)} TTC</b>
											)}
										</>
									)}
									.
								</p>
							)}
						</>
					)}

				{startDate && deliveryDate && (
					<p>
						Les travaux devraient être réalisés entre le{" "}
						<b>{format(startDate, "dd/MM/yyyy")}</b> et le{" "}
						<b>{format(deliveryDate, "dd/MM/yyyy")}</b>.
					</p>
				)}

				{cityHallStartDate && (
					<p>
						Une déclaration d'ouverture de chantier a été déposée en mairie pour
						un démarrage au <b>{format(cityHallStartDate, "dd/MM/yyyy")}</b>.
					</p>
				)}
			</div>
		</div>
	);
};

export default OperationDescriptiveBlock;
